import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { ToastRegionProps, ToastRegion } from './ToastRegion';
import { ToastContext, ToastData } from './context';
import { useThemeCheck } from '../../utils';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { ToastQueue } from './ToastQueue';

function wrapInViewTransition(fn: () => void): void {
  if ('startViewTransition' in document) {
    (document as any)
      .startViewTransition({
        update: () => {
          flushSync(fn);
        },
      })
      .ready.catch(() => {});
  } else {
    fn();
  }
}

export type ToastProviderProps = ToastRegionProps & {
  children: React.ReactNode;
};

/**
 * Sapphire toast provider.
 * Render this at the root of your app.
 */
export const ToastProvider = ({
  children,
  placement = 'bottom',
  ...props
}: ToastProviderProps): React.JSX.Element => {
  useThemeCheck();

  const [queue] = useState(
    () =>
      new ToastQueue<ToastData>({
        maxVisibleToasts: Infinity,
        wrapUpdate: wrapInViewTransition,
      })
  );

  const baseOverlayState = useOverlayTriggerState({});

  // Wrap overlayState methods with view transitions
  const overlayState = React.useMemo(
    () => ({
      ...baseOverlayState,
      toggle: () =>
        wrapInViewTransition(() => {
          if (queue.visibleToasts.length <= 1) {
            return;
          }
          baseOverlayState.toggle();
        }),
      open: () => wrapInViewTransition(() => baseOverlayState.open()),
      close: () => wrapInViewTransition(() => baseOverlayState.close()),
    }),
    [baseOverlayState, queue]
  );

  // Set the state to collapsed whenever the queue is emptied.
  useEffect(() => {
    return queue.subscribe(() => {
      if (queue.visibleToasts.length <= 1 && overlayState.isOpen) {
        overlayState.close();
      }
    });
  }, [queue, overlayState]);

  return (
    <ToastContext.Provider value={{ queue, overlayState }}>
      {children}
      <ToastRegion {...props} placement={placement} />
    </ToastContext.Provider>
  );
};
